import { Injectable, inject, signal } from '@angular/core';
import { StorefrontCartStateService } from './storefront-cart-state.service';

const CART_KEY = 'coreshop_cart_session';
const AUTH_KEY = 'coreshop_customer_auth';
/** Conversación del asistente de un comprador con sesión (la clave lleva un hash del correo, no el correo). */
const CHAT_KEY = 'sf-chat';
/** Registro pendiente de verificar: en sessionStorage, solo vive en esta pestaña. */
const PENDING_REGISTRATION_KEY = 'coreshop_pending_registration';

export interface PendingRegistration {
  registrationToken: string;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class StorefrontSessionService {
  private readonly cartState = inject(StorefrontCartStateService);
  /** Se incrementa al login/logout para refrescar la UI del header. */
  private readonly _authVersion = signal(0);
  readonly authVersion = this._authVersion.asReadonly();
  /** Conversación del visitante anónimo: solo en memoria, se reinicia al recargar. */
  private readonly anonymousChat = new Map<number, string>();

  getCartSession(companyId: number): string | null {
    const raw = localStorage.getItem(`${CART_KEY}_${companyId}`);
    return raw || null;
  }

  setCartSession(companyId: number, token: string): void {
    localStorage.setItem(`${CART_KEY}_${companyId}`, token);
  }

  /** Invalida la sesión de carrito (p. ej. tras checkout) para forzar uno vacío nuevo. */
  clearCartSession(companyId: number): void {
    localStorage.removeItem(`${CART_KEY}_${companyId}`);
  }

  getAuth(companyId: number): { accessToken: string; email: string } | null {
    const raw = localStorage.getItem(`${AUTH_KEY}_${companyId}`);
    if (!raw) return null;
    try {
      const auth = JSON.parse(raw) as { accessToken: string; email: string };
      // El JWT de cliente dura 7 días: vencido, la tienda no debe seguir mostrándose logueada.
      if (isJwtExpired(auth.accessToken)) {
        localStorage.removeItem(`${AUTH_KEY}_${companyId}`);
        return null;
      }
      return auth;
    } catch {
      return null;
    }
  }

  setAuth(companyId: number, accessToken: string, email: string): void {
    localStorage.setItem(`${AUTH_KEY}_${companyId}`, JSON.stringify({ accessToken, email }));
    this._authVersion.update(v => v + 1);
  }

  /**
   * #FUNCIONALIDAD Registro de «Crear cuenta» esperando el código del correo. El token identifica el registro y prueba
   * que esta pestaña es la que lo creó; sin él, la vista del código pide retomar con el correo y la contraseña.
   */
  getPendingRegistration(companyId: number): PendingRegistration | null {
    try {
      const raw = sessionStorage.getItem(`${PENDING_REGISTRATION_KEY}_${companyId}`);
      return raw ? JSON.parse(raw) as PendingRegistration : null;
    } catch {
      return null;
    }
  }

  setPendingRegistration(companyId: number, pending: PendingRegistration): void {
    try {
      sessionStorage.setItem(`${PENDING_REGISTRATION_KEY}_${companyId}`, JSON.stringify(pending));
    } catch {
      // Sin almacenamiento (navegación privada estricta): la vista del código ofrece retomar con la contraseña.
    }
  }

  clearPendingRegistration(companyId: number): void {
    try {
      sessionStorage.removeItem(`${PENDING_REGISTRATION_KEY}_${companyId}`);
    } catch {
      // Nada que limpiar.
    }
  }

  clearAuth(companyId: number): void {
    localStorage.removeItem(`${AUTH_KEY}_${companyId}`);
    this._authVersion.update(v => v + 1);
  }

  /**
   * Cierre de sesión del cliente: además del token de acceso descarta la sesión de carrito
   * (ya quedó asociada a su cuenta) y reinicia el badge, para que el siguiente visitante del
   * mismo navegador empiece con un carrito vacío.
   */
  signOut(companyId: number): void {
    this.clearChatSession(companyId);
    this.clearCartSession(companyId);
    this.cartState.reset(companyId);
    this.clearAuth(companyId);
  }

  /**
   * #FUNCIONALIDAD Sesión del chat del asistente. Con cuenta iniciada se guarda por comprador y su historial
   * se mantiene al recargar; un visitante anónimo la tiene solo en memoria (al recargar empieza de cero) y
   * al cerrar sesión se borra. Antes se guardaba por tienda en el navegador y el siguiente visitante veía
   * la conversación anterior.
   */
  chatSessionToken(companyId: number): string {
    localStorage.removeItem(`${CHAT_KEY}-${companyId}`); // clave anterior, compartida por todos los visitantes
    const auth = this.getAuth(companyId);
    if (!auth) {
      let token = this.anonymousChat.get(companyId);
      if (!token) {
        token = newChatToken();
        this.anonymousChat.set(companyId, token);
      }
      return token;
    }
    const key = this.customerChatKey(companyId, auth.email);
    let token = localStorage.getItem(key);
    if (!token) {
      token = newChatToken();
      localStorage.setItem(key, token);
    }
    return token;
  }

  /** Guarda el identificador que devolvió el servidor, con la misma regla (cuenta o memoria). */
  rememberChatSession(companyId: number, token: string): void {
    const auth = this.getAuth(companyId);
    if (auth) localStorage.setItem(this.customerChatKey(companyId, auth.email), token);
    else this.anonymousChat.set(companyId, token);
  }

  /** Borra la conversación guardada del comprador actual y la anónima en memoria. */
  clearChatSession(companyId: number): void {
    const auth = this.getAuth(companyId);
    if (auth) localStorage.removeItem(this.customerChatKey(companyId, auth.email));
    this.anonymousChat.delete(companyId);
  }

  private customerChatKey(companyId: number, email: string): string {
    return `${CHAT_KEY}-${companyId}-${hash(email.trim().toLowerCase())}`;
  }
}

/** true solo si el token trae `exp` y ya pasó; un token ilegible se deja validar al backend. */
function isJwtExpired(token: string | undefined): boolean {
  const payload = token?.split('.')[1];
  if (!payload) return false;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

function newChatToken(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** Hash corto (djb2) para no dejar el correo del comprador en las claves del navegador. */
function hash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
